import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, TextField, Typography, CircularProgress, Box } from '@mui/material';
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';

const CertificationExpiredOverlay = ({ open }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [certificateText, setCertificateText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState('');

  const handleSubmit = () => {
    if (ws) {
      setIsSubmitting(true);
      setSuccessMessage('');
      setErrorReason('');
      setErrorMessage('');
      
      ws.send(JSON.stringify({ command: 'updateCertificate', certificate: certificateText }));
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      if (parsedMessage.command === "updatedCertificate") {
        setIsSubmitting(false);
        if (parsedMessage?.data.status === "success") {
          // Once page is reloaded, the overlay will be closed if the certificate is valid, or the overlay will be shown again if the certificate is invalid
          setSuccessMessage(t('certification_expired_overlay.certificate_validated'));
          window.location.reload();
        } else {
          setErrorMessage(t('certification_expired_overlay.error_message'));
          setErrorReason(parsedMessage?.data.message);
        }
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [subscribeToMessages, t])

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent dismissal regardless of reason
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2000 }
      }}
    >
      <DialogTitle>{t('certification_expired_overlay.certificate_invalid_title')}</DialogTitle>
      <DialogContent>
        {t('certification_expired_overlay.certificate_invalid_message')}
        <TextField
          label={t('certification_expired_overlay.new_certificate')}
          fullWidth
          margin="normal"
          value={certificateText}
          style={{ marginBottom: '1rem' }}
          onChange={(e) => setCertificateText(e.target.value)}
        />
        {errorMessage && (
          <Typography color="error" variant="body2" style={{ marginBottom: '0.5rem' }}>
            {errorMessage}
          </Typography>
        )}
        {errorReason && (
          <Typography color="error" variant="body2" style={{ marginBottom: '1rem' }}>
            {errorReason}
          </Typography>
        )}
        {successMessage && (
          <Typography color="success" variant="body2" style={{ marginBottom: '1rem' }}>
            {successMessage}
          </Typography>
        )}
        {/* when submitting, show a loading spinner */}
        {isSubmitting && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right' }}>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
            {t('certification_expired_overlay.submit')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};


export default CertificationExpiredOverlay;
